import React from "react";
import { Link } from "react-router-dom";
import { DownloadSimple } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MATRIX = [
  {
    law: "PIPA ss. 5–6 — Accountability & policies",
    how: "A designated Privacy Officer (Andrew Kiedyk, admin@elevation650.com) is accountable for compliance. Our privacy policies, including our use of service providers outside Canada, are published and available to anyone on request.",
  },
  {
    law: "PIPA ss. 7–8 & 13 — Consent & notification",
    how: "Public customers consent at booking, purchase, or account creation, and we state the purposes of collection at that point. School-seat student information is collected under the authority of the contracting school board (see POPA row below).",
  },
  {
    law: "PIPA s. 13.1 — Service providers outside Canada",
    how: "We disclose every provider and its country (see table below), and name a contact who can answer questions about foreign handling of personal information: admin@elevation650.com.",
  },
  {
    law: "PIPA s. 34 — Safeguards",
    how: "All traffic encrypted in transit (TLS). Passwords stored as salted bcrypt hashes only. Role-based access (admin / teacher / student). Administrative actions on sensitive data are audit-logged. Database is access-controlled and not publicly reachable.",
  },
  {
    law: "PIPA s. 34.1 — Breach notification",
    how: "Documented incident response: contain, assess for real risk of significant harm, notify the OIPC without unreasonable delay where required, notify affected individuals, and notify any affected school board — target within 72 hours of discovery.",
  },
  {
    law: "PIPA s. 35 — Retention",
    how: "Published retention schedule: learning data deleted within 60 days of a verified request or contract end; bookings/messages up to 2 years; waivers kept for legal limitation periods; financial records 7 years (CRA).",
  },
  {
    law: "PIPA ss. 24–25 — Access & correction",
    how: "Individuals may request a copy of their personal information or corrections by emailing admin@elevation650.com. We respond within 45 days as the Act requires.",
  },
  {
    law: "POPA (formerly FOIP) — Service provider duties to public bodies",
    how: "For school boards we operate as a service provider: student data is used only to deliver the purchased training — never for advertising, profiling, resale, or AI training. We sign board data-sharing / service agreements, notify boards of privacy incidents within 72 hours, limit access to authorized staff, use no subcontractors beyond the disclosed providers below, and return or securely destroy student data at contract end or on request. We support board Privacy Impact Assessments with our downloadable privacy package.",
  },
  {
    law: "AI Coach — automated advice chat (PIPA ss. 6–8, 13)",
    how: "The optional in-app \u201cCoach\u201d chat sends the user's message (and optional self-selected sex/grade) to an AI language-model provider (OpenAI, via Emergent, USA) to generate advice grounded in the course lessons — disclosed on the chat screen itself. Conversations are transient: shortly after a chat goes quiet, an anonymized summary (issue category, lessons referenced, curriculum-gap notes — identifying details stripped) is kept and the transcript is permanently deleted. Never used for advertising, profiling, resale, or AI training. A crisis screen shows Kids Help Phone / 988 / 911 resources and alerts our safety contact if a message mentions self-harm, abuse or danger. Boards may ask us to disable Coach for their seats.",
  },
  {
    law: "PIPEDA (Canada) — Cross-border & interprovincial data",
    how: "Alberta's PIPA is declared substantially similar to PIPEDA, so PIPA governs our handling of personal information within Alberta. PIPEDA continues to apply when personal information crosses provincial or national borders — e.g., our disclosed U.S. service providers below. Those flows carry the same safeguards, contractual limits, and disclosure (PIPA s. 13.1; PIPEDA accountability and safeguards principles).",
  },
  {
    law: "CASL — Anti-spam (texts & email)",
    how: "Promotional texts (e.g., discount codes) are sent only to existing customers or people who requested them, with consent recorded at the time of sending. Every message identifies us and supports STOP to opt out. Transactional messages (bookings, receipts, course access) are sent as part of the service.",
  },
];

const PROVIDERS = [
  ["Stripe", "Payment processing", "United States"],
  ["OpenAI (via Emergent)", "AI Coach chat, voice transcription & read-aloud (transcripts deleted after anonymized summary)", "United States"],
  ["Twilio", "Text messages (sending & receiving)", "United States"],
  ["Resend", "Transactional email", "United States"],
  ["Google", "Business calendar scheduling only", "United States"],
  ["Secure cloud hosting", "App hosting & encrypted database", "United States"],
];

export default function CompliancePage() {
  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-5 py-14" data-testid="compliance-page">
        <Link to="/" className="text-[#FFE01A] text-sm font-bold uppercase tracking-wide no-underline">&larr; selfagency.ca</Link>
        <h1 className="text-4xl sm:text-5xl font-extrabold font-display mt-4 mb-1" style={{ color: "#fff" }}>Privacy & Schools</h1>
        <p className="text-[#A1A1AA] text-xs mb-6">How The Self-Agency Project complies with Alberta's privacy laws — PIPA and POPA (formerly FOIP) — plus Canada's PIPEDA and CASL.</p>

        <a href={`${API}/site/privacy-package.pdf`} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 bg-[#FFE01A] text-black font-bold text-sm px-4 py-2 rounded-full no-underline hover:opacity-90"
          data-testid="privacy-package-download">
          <DownloadSimple size={16} weight="bold" /> Download the School Board Privacy Package (PDF)
        </a>

        <h2 className="text-lg font-bold font-display mt-10 mb-2" style={{ color: "#fff" }}>In plain language</h2>
        <p className="text-[#D4D4D8] text-sm leading-relaxed mb-2">
          We collect the minimum needed to deliver training: a student's name, email, and course progress. We never sell data,
          never show ads, never profile students, and never use student information to train AI. School boards stay in control
          of their students' information — we act as their service provider under Alberta's Protection of Privacy Act (POPA,
          which replaced FOIP in June 2025), and our own handling of personal information is governed by Alberta's PIPA.
        </p>

        <h2 className="text-lg font-bold font-display mt-8 mb-3" style={{ color: "#fff" }}>Compliance summary</h2>
        <div className="space-y-4">
          {MATRIX.map((row, i) => (
            <div key={i} className="border border-white/10 rounded-xl p-4" data-testid={`compliance-row-${i}`}>
              <div className="text-[#FFE01A] text-xs font-bold uppercase tracking-wide mb-1">{row.law}</div>
              <p className="text-[#D4D4D8] text-sm leading-relaxed m-0">{row.how}</p>
            </div>
          ))}
        </div>

        <h2 className="text-lg font-bold font-display mt-10 mb-3" style={{ color: "#fff" }}>Service providers (subprocessors)</h2>
        <div className="border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="subprocessor-table">
            <thead>
              <tr className="text-left text-[#A1A1AA] text-xs uppercase tracking-wide">
                <th className="px-3 py-2 border-b border-white/10">Provider</th>
                <th className="px-3 py-2 border-b border-white/10">Purpose</th>
                <th className="px-3 py-2 border-b border-white/10">Country</th>
              </tr>
            </thead>
            <tbody>
              {PROVIDERS.map(([p, u, c], i) => (
                <tr key={i} className="text-[#D4D4D8]">
                  <td className="px-3 py-2 border-b border-white/5 font-semibold">{p}</td>
                  <td className="px-3 py-2 border-b border-white/5">{u}</td>
                  <td className="px-3 py-2 border-b border-white/5">{c}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[#A1A1AA] text-xs mt-2">Each provider receives only the minimum information needed for its function (PIPA s. 13.1 disclosure).</p>

        <h2 className="text-lg font-bold font-display mt-10 mb-2" style={{ color: "#fff" }}>Privacy Officer</h2>
        <p className="text-[#D4D4D8] text-sm leading-relaxed mb-2">
          Andrew Kiedyk · Elevation650 · <a href="mailto:admin@elevation650.com" className="text-[#FFE01A]">admin@elevation650.com</a> · +1 780-228-5325.
          Boards are welcome to send their data-sharing agreements and security questionnaires — we complete and sign them as part of onboarding.
        </p>

        <p className="text-[#71717A] text-xs mt-8">
          This page summarizes our practices for information purposes and is not legal advice. Full details are in our{" "}
          <Link to="/privacy" className="text-[#A1A1AA]">Privacy Policy</Link>.
        </p>

        <div className="border-t border-white/10 pt-6 mt-10 text-xs text-[#A1A1AA]">
          © {new Date().getFullYear()} Elevation650 · <Link to="/privacy" className="text-[#A1A1AA]">Privacy Policy</Link> · <Link to="/terms" className="text-[#A1A1AA]">Terms of Service</Link>
        </div>
      </div>
    </div>
  );
}
